import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { assessmentQuery, downloadEvidence, uploadEvidence, type AssessmentPage, type EvidenceFile } from '../api/dnscAssessment'

/**
 * Documentul unei cerinte, incarcat in platforma (serverul de fisiere), langa
 * numele + link-ul de pana acum. Incarcarea nu salveaza raspunsul: fisierul se
 * leaga la "Save", ca restul formularului.
 *
 * Fara serverul de fisiere configurat (`uploads.enabled` fals) campul nu apare
 * deloc - ramane numai nume + link.
 */
export function EvidenceUpload({
  code,
  file,
  disabled,
  onChange,
}: {
  code: string
  file: EvidenceFile | null
  disabled: boolean
  onChange: (file: EvidenceFile | null) => void
}) {
  const client = useQueryClient()
  const uploads = client.getQueryData<AssessmentPage>(assessmentQuery.queryKey)?.uploads
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!uploads?.enabled) return null

  const pick = (picked: File | undefined) => {
    if (!picked) return
    setError('')
    if (picked.size > uploads.max_mb * 1024 * 1024) {
      setError(`the document is larger than ${uploads.max_mb} MB`)
      return
    }
    setBusy(true)
    uploadEvidence(code, picked)
      .then((out) => {
        if (!out.ok || !out.file) setError(out.error ?? 'not uploaded')
        else onChange(out.file)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'not uploaded'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="nis2-sa-field nis2-sa-field-wide" data-sa-upload>
      Upload the document (optional, up to {uploads.max_mb} MB: {uploads.types.join(', ')})
      <div className="nis2-sa-upload">
        {file ? (
          <>
            <button type="button" className="btn btn-link btn-xs" data-sa-file={file.id} onClick={() => void downloadEvidence(file).catch((e: unknown) => setError(e instanceof Error ? e.message : 'not read'))}>
              <i className="fas fa-paperclip" aria-hidden="true"></i> {file.name}
            </button>
            <button type="button" className="btn btn-default btn-xs" disabled={disabled} data-sa-file-remove onClick={() => onChange(null)}>
              Detach
            </button>
          </>
        ) : null}
        <input
          type="file"
          className="input-sm"
          disabled={disabled || busy}
          accept={uploads.types.map((t) => `.${t}`).join(',')}
          data-sa-file-input
          onChange={(e) => {
            pick(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        {busy ? <span className="nis2-muted">uploading...</span> : null}
        {error ? (
          <span className="nis2-sa-error" data-sa-upload-error>
            {error}
          </span>
        ) : null}
      </div>
    </div>
  )
}
